import { useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Tooltip,
} from "@mui/material";

const HEADER_ROW1_HEIGHT = 40;
const HEADER_ROW2_HEIGHT = 40;
const MIN_COL_WIDTH = 60;

const headerCellSx = {
  fontWeight: 700,
  py: { xs: "4px", sm: "6px", md: "8px", lg: "12px" },
  px: { xs: "4px", sm: "6px", md: "8px", lg: "12px" },
  whiteSpace: "nowrap",
  borderBottom: "none",
  borderRight: "1px solid rgba(255, 255, 255, 0.2)",
  height: HEADER_ROW1_HEIGHT,
  boxSizing: "border-box",
  textAlign: "center",
  fontSize: { xs: "7px", md: "8px", lg: "12px" },
  lineHeight: "140%",
  color: "#fff",
  position: "relative",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "&:last-child": {
    borderRight: "none",
  },
};

const bodyCellSx = {
  py: { xs: "4px", sm: "6px", md: "8px", lg: "12px" },
  px: { xs: "4px", sm: "6px", md: "8px", lg: "12px" },
  fontSize: { xs: "8px", md: "10px", lg: "14px" },
  lineHeight: "150%",
  textAlign: "center",
  borderBottom: "1px solid #E8ECF0",
  borderRight: "none",
  color: "#1A2025",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const cellTextSx = {
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
};

const resizeHandleStyle = {
  position: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  width: "6px",
  cursor: "col-resize",
  zIndex: 10,
  background: "transparent",
  "&:hover": {
    background: "rgba(255,255,255,0.3)",
  },
};

export default function OrderOverviewTable({
  headerConfig = [],
  rows = [],
  colors = {},
  loading,
  maxHeight,
}) {
  const tableContainerRef = useRef(null);
  const resizeRef = useRef(null);

  // Build flat list of leaf header cells (first row for simple headers)
  const leafCells = headerConfig[0]?.cells || [];

  const [colWidths, setColWidths] = useState(() =>
    leafCells.map((c) => c.width || 120)
  );

  // Sync widths if header columns change
  useEffect(() => {
    if (leafCells.length !== colWidths.length) {
      setColWidths(leafCells.map((c, i) => colWidths[i] ?? c.width ?? 120));
    }
  }, [leafCells.length]);

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const onResizeStart = useCallback(
    (colIndex, e) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startWidth = colWidths[colIndex];
      resizeRef.current = { colIndex, startX, startWidth };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMouseMove = (moveEvent) => {
        const { colIndex: idx, startX: sx, startWidth: sw } =
          resizeRef.current;
        const newWidth = Math.max(MIN_COL_WIDTH, sw + (moveEvent.clientX - sx));
        setColWidths((prev) => {
          const next = [...prev];
          next[idx] = newWidth;
          return next;
        });
      };

      const onMouseUp = () => {
        resizeRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [colWidths]
  );

  if (!headerConfig?.length && !rows?.length && !loading) {
    return null;
  }

  const getHeaderBg = (group) =>
    group === "mid"
      ? colors.headerMid
      : group === "light"
        ? colors.headerLight
        : colors.headerDark;

  const getHeaderBorderColor = (group) =>
    group === "mid"
      ? colors.borderMid
      : group === "light"
        ? colors.borderLight
        : colors.borderDark;

  const getHeaderTextColor = (group) =>
    group === "mid"
      ? colors.textMid
      : group === "light"
        ? colors.textLight
        : colors.textDark;

  const roundup = (value, min = 0, digits = 2) => {
    if (value === null || value === undefined) return min;
    return Number(value).toFixed(digits);
  };

 const formatCell = (value) => {
   if (value === null || value === undefined) return "-";

   if (typeof value === "string" && value.includes("T")) {
     const date = new Date(value);
     if (!isNaN(date.getTime())) {
       return date.toISOString().split("T")[0];
     }
   }

   if (typeof value === "number" || (!isNaN(value) && value !== "")) {
     return roundup(Number(value), 0, 0);
   }

   return value;
 };
  const naturalWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const shouldExpand = containerWidth > naturalWidth && naturalWidth > 0;
  const scale = shouldExpand ? containerWidth / naturalWidth : 1;
  const displayWidths = colWidths.map((w) => Math.floor(w * scale));
  const totalWidth = shouldExpand ? containerWidth : naturalWidth;

  return (
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      elevation={0}
      sx={{
        maxHeight: maxHeight || { sm: 160, md: 200, lg: 300 },
        borderRadius: 2,
        overflow: "auto",
        border: "1px solid #E8ECF0",
      }}
    >
      <Table
        stickyHeader
        sx={{
          width: totalWidth,
          minWidth: "100%",
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableHead>
          {headerConfig.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              {row.cells.map((cell, i) => {
                const group = cell.parent ?? cell.group;
                const isFirstRow = rowIndex === 0;
                return (
                  <TableCell
                    key={i}
                    rowSpan={cell.rowSpan}
                    colSpan={cell.colSpan}
                    sx={{
                      ...headerCellSx,
                      bgcolor: getHeaderBg(group),
                      color: getHeaderTextColor(group),
                      width: isFirstRow ? displayWidths[i] : undefined,
                      minWidth: isFirstRow ? displayWidths[i] : undefined,
                      maxWidth: isFirstRow ? displayWidths[i] : undefined,
                      top: rowIndex === 0 ? 0 : HEADER_ROW1_HEIGHT,
                      zIndex: rowIndex === 0 ? 7 : 6,
                      height:
                        rowIndex === 0
                          ? HEADER_ROW1_HEIGHT
                          : HEADER_ROW2_HEIGHT,
                      borderRight: `1px solid #D9E1E7`,
                      ...(group && {
                        borderRight: `1px solid ${getHeaderBorderColor(group)}`,
                        borderBottom: `1px solid ${getHeaderBorderColor(group)}`,
                      }),
                    }}
                  >
                    {cell.label}
                    {isFirstRow && (
                      <Box
                        component="span"
                        sx={resizeHandleStyle}
                        onMouseDown={(e) => onResizeStart(i, e)}
                      />
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {loading ? (
            rows.map((row) => (
              <TableRow key={row.id}>
                {row.cells.map((cell, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      ...bodyCellSx,
                      width: displayWidths[i],
                    }}
                  >
                    <Skeleton variant="text" height={20} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 || headerConfig.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={100}
                align="center"
                sx={{ ...bodyCellSx, py: 4 }}
              >
                No rows available
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                sx={{
                  "&:hover": { backgroundColor: "#F5F8FA" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                {row.cells.map((cell, i) => {
                  const display = formatCell(cell.value);
                  const raw = String(cell.value ?? "-");
                  return (
                    <Tooltip
                      key={i}
                      title={raw}
                      placement="left"
                      enterDelay={300}
                      disableHoverListener={raw.length <= 20}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            color: "#000",
                            maxWidth: 320,
                            fontSize: "12px",
                            lineHeight: "160%",
                            p: "6px 10px",
                            borderRadius: "6px",
                          },
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          width: displayWidths[i],
                          borderRight:
                            i === row.cells.length - 1
                              ? "none"
                              : "1px solid #E0E0E0",
                        }}
                      >
                        <Box sx={cellTextSx}>{display}</Box>
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
